import React, { createContext, useContext, useState, useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { BookOpen, Calculator, CreditCard, Users, Package, FileText, TrendingUp, Settings, Building, Globe } from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { useTheme } from '@/contexts/ThemeContext'
import { ThemedCard } from '@/components/ui/ThemedCard'

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:55421'
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'sb_publishable_ACJWlzQHlZjBrEguHvfOxg_3BJgxAaH'
const supabase = createClient(supabaseUrl, supabaseKey)

// Entity Context for company management
export const EntityContext = createContext<{
  currentEntity: any
  entities: any[]
  switchEntity: (entityId: string) => void
  loading: boolean
}>({
  currentEntity: null,
  entities: [],
  switchEntity: () => {},
  loading: true
})

export const EntityProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentEntity, setCurrentEntity] = useState<any>(null)
  const [entities, setEntities] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadEntities()
  }, [])

  const loadEntities = async () => {
    try {
      // Try to load from database first
      const { data, error } = await supabase
        .from('entities')
        .select('*')
        .eq('is_active', true)
        .order('legal_name')

      if (error) {
        console.warn('Database not ready, using mock data:', error)
        // Use mock data if database isn't ready
        setEntities([
          { id: '1', legal_name: 'HALE\'S AIR CONDITIONING SERVICE, INC.', address_city: 'St. Petersburg', address_state: 'FL' },
          { id: '2', legal_name: 'CLIMATE CONTROL SERVICES, INC.', address_city: 'Boynton Beach', address_state: 'FL' },
          { id: '3', legal_name: 'Abraham AC', address_city: 'Oakland Park', address_state: 'FL' },
          { id: '4', legal_name: 'Right Away Home Warranty', address_city: 'Boynton Beach', address_state: 'FL' },
          { id: '5', legal_name: 'Right Away Contact Center', address_city: 'Tampa', address_state: 'FL' },
          { id: '6', legal_name: 'Right Away Home Services LP', address_city: 'St. Petersburg', address_state: 'FL' }
        ])
      } else {
        setEntities(data || [])
      }

      // Set first entity as default
      const entitiesToUse = data || [
        { id: '1', legal_name: 'HALE\'S AIR CONDITIONING SERVICE, INC.', address_city: 'St. Petersburg', address_state: 'FL' },
        { id: '2', legal_name: 'CLIMATE CONTROL SERVICES, INC.', address_city: 'Boynton Beach', address_state: 'FL' },
        { id: '3', legal_name: 'Abraham AC', address_city: 'Oakland Park', address_state: 'FL' },
        { id: '4', legal_name: 'Right Away Home Warranty', address_city: 'Boynton Beach', address_state: 'FL' },
        { id: '5', legal_name: 'Right Away Contact Center', address_city: 'Tampa', address_state: 'FL' },
        { id: '6', legal_name: 'Right Away Home Services LP', address_city: 'St. Petersburg', address_state: 'FL' }
      ]

      if (entitiesToUse.length > 0) {
        setCurrentEntity(entitiesToUse[0])
      }
    } catch (error) {
      console.error('Error loading entities, using mock data:', error)
      // Fallback to mock data
      setEntities([
        { id: '1', legal_name: 'HALE\'S AIR CONDITIONING SERVICE, INC.', address_city: 'St. Petersburg', address_state: 'FL' },
        { id: '2', legal_name: 'CLIMATE CONTROL SERVICES, INC.', address_city: 'Boynton Beach', address_state: 'FL' },
        { id: '3', legal_name: 'Abraham AC', address_city: 'Oakland Park', address_state: 'FL' },
        { id: '4', legal_name: 'Right Away Home Warranty', address_city: 'Boynton Beach', address_state: 'FL' },
        { id: '5', legal_name: 'Right Away Contact Center', address_city: 'Tampa', address_state: 'FL' },
        { id: '6', legal_name: 'Right Away Home Services LP', address_city: 'St. Petersburg', address_state: 'FL' }
      ])
      setCurrentEntity({ id: '1', legal_name: 'HALE\'S AIR CONDITIONING SERVICE, INC.', address_city: 'St. Petersburg', address_state: 'FL' })
    } finally {
      setLoading(false)
    }
  }

  const switchEntity = (entityId: string) => {
    const entity = entities.find(e => e.id === entityId)
    if (entity) {
      setCurrentEntity(entity)
    }
  }

  return (
    <EntityContext.Provider value={{
      currentEntity,
      entities,
      switchEntity,
      loading
    }}>
      {children}
    </EntityContext.Provider>
  )
}

// Company Selector Component
const CompanySelector = () => {
  const { currentEntity, entities, switchEntity } = useContext(EntityContext)
  const { theme } = useTheme()

  if (!currentEntity) {
    return (
      <div className="flex items-center gap-2">
        <Building className="h-4 w-4" style={{ color: theme.colors.textTertiary }} />
        <span className="text-sm" style={{ color: theme.colors.textTertiary }}>Loading companies...</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Building className="h-4 w-4" style={{ color: theme.colors.textTertiary }} />
        <select
          value={currentEntity.id}
          onChange={(e) => switchEntity(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{
            backgroundColor: theme.colors.bgCard,
            borderColor: theme.colors.borderColor,
            color: theme.colors.textPrimary
          }}
        >
          {entities.map(entity => (
            <option key={entity.id} value={entity.id}>
              {entity.legal_name}
            </option>
          ))}
          <option value="combined">🌐 Combined View</option>
        </select>
      </div>

      {currentEntity.id === 'combined' ? (
        <div className="flex items-center gap-2" style={{ color: theme.colors.primaryColor }}>
          <Globe className="h-4 w-4" />
          <span className="text-sm font-medium">Combined View</span>
        </div>
      ) : (
        <div className="text-sm" style={{ color: theme.colors.textTertiary }}>
          {currentEntity.address_city}, {currentEntity.address_state}
        </div>
      )}
    </div>
  )
}

// Import accounting pages
import ChartOfAccountsPage from './pages/ChartOfAccountsPage'
import ManualJournalPage from './pages/ManualJournalPage'
import InvoicesPage from './pages/InvoicesPage'
import BillsPage from './pages/BillsPage'
import InventoryPage from './pages/InventoryPage'
import ReportsPage from './pages/ReportsPage'
import PeriodClosePage from './pages/PeriodClosePage'

const AccountingModule = () => {
  const { theme } = useTheme()

  return (
    <EntityProvider>
      <div className="space-y-6">
        {/* Header with Company Selection */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>Accounting</h1>
              <p style={{ color: theme.colors.textSecondary }}>GAAP-compliant financial management</p>
            </div>

            {/* Company Selection */}
            <CompanySelector />
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Current Period</div>
              <div className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>2025-10</div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ThemedCard padding="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Total Assets</div>
                <div className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>$245,678</div>
              </div>
            </div>
          </ThemedCard>

          <ThemedCard padding="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>AR Balance</div>
                <div className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>$45,230</div>
              </div>
            </div>
          </ThemedCard>

          <ThemedCard padding="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <CreditCard className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>AP Balance</div>
                <div className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>$23,450</div>
              </div>
            </div>
          </ThemedCard>

          <ThemedCard padding="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Package className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Inventory Value</div>
                <div className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>$67,890</div>
              </div>
            </div>
          </ThemedCard>
        </div>

        {/* Main Routes */}
        <Routes>
          <Route index element={<AccountingDashboard />} />
          <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
          <Route path="journals" element={<ManualJournalPage />} />
          <Route path="ar/invoices" element={<InvoicesPage />} />
          <Route path="ap/bills" element={<BillsPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="period-close" element={<PeriodClosePage />} />
        </Routes>
      </div>
    </EntityProvider>
  )
}

// Dashboard Component
const AccountingDashboard = () => {
  const { theme } = useTheme()

  const quickActions = [
    {
      title: 'Chart of Accounts',
      description: 'Manage account codes and structure',
      icon: BookOpen,
      href: '/accounting/chart-of-accounts',
      color: 'bg-blue-500/10 text-blue-400'
    },
    {
      title: 'Manual Journal',
      description: 'Create adjusting entries',
      icon: Calculator,
      href: '/accounting/journals',
      color: 'bg-green-500/10 text-green-400'
    },
    {
      title: 'AR Management',
      description: 'Invoices and payments',
      icon: Users,
      href: '/accounting/ar/invoices',
      color: 'bg-purple-500/10 text-purple-400'
    },
    {
      title: 'AP Management',
      description: 'Bills and payments',
      icon: CreditCard,
      href: '/accounting/ap/bills',
      color: 'bg-red-500/10 text-red-400'
    },
    {
      title: 'Inventory',
      description: 'Parts and equipment',
      icon: Package,
      href: '/accounting/inventory',
      color: 'bg-orange-500/10 text-orange-400'
    },
    {
      title: 'Financial Reports',
      description: 'Trial balance and statements',
      icon: FileText,
      href: '/accounting/reports',
      color: 'bg-indigo-500/10 text-indigo-400'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <ThemedCard
              key={action.title}
              hoverable
              onClick={() => window.location.href = action.href}
              padding="p-6"
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${action.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                    {action.title}
                  </h3>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                    {action.description}
                  </p>
                </div>
              </div>
            </ThemedCard>
          )
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ThemedCard>
          <h3 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Recent Journal Entries</h3>
          <div className="space-y-3">
            {[
              { type: 'AR Invoice', amount: '$1,250.00', date: '2025-10-15' },
              { type: 'Cash Receipt', amount: '$1,250.00', date: '2025-10-14' },
              { type: 'AP Bill', amount: '$450.00', date: '2025-10-13' }
            ].map((entry, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: theme.colors.borderColor + '80' }}>
                <div>
                  <div className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>{entry.type}</div>
                  <div className="text-xs" style={{ color: theme.colors.textTertiary }}>{entry.date}</div>
                </div>
                <div className="text-sm font-semibold text-green-400">{entry.amount}</div>
              </div>
            ))}
          </div>
        </ThemedCard>

        <ThemedCard>
          <h3 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Period Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: theme.colors.textSecondary }}>Current Period</span>
              <span className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>October 2025</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: theme.colors.textSecondary }}>Status</span>
              <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs font-medium rounded-full">
                Open
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: theme.colors.textSecondary }}>Trial Balance</span>
              <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs font-medium rounded-full">
                Balanced
              </span>
            </div>
            <div className="pt-2">
              <button 
                className="w-full px-4 py-2 text-white text-sm font-medium rounded-md transition-colors"
                style={{ backgroundColor: theme.colors.primaryColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
              >
                Close Period
              </button>
            </div>
          </div>
        </ThemedCard>
      </div>
    </div>
  )
}

export default AccountingModule
